import { Alert, Checkbox, Input, message, Modal, Typography } from 'antd'
import { useDuplicateActiveThreads } from 'features/libra/comments/commentsApi'
import {
  LibraTicket,
  useCreateTicketVersion,
} from 'features/libra/ticket/libraTicketsApi'
import { FC, useCallback, useState } from 'react'
import styled from 'styled-components'

const { Text } = Typography
const { TextArea } = Input

export const CreateVersionModal: FC<{
  isOpen: boolean
  sourceTicket?: LibraTicket
  onClose: () => void
  onSuccess: (
    newTicketId: string,
    sourceTicket: LibraTicket
  ) => void | Promise<void>
}> = ({ isOpen, sourceTicket, onClose, onSuccess }) => {
  const createTicketVersion = useCreateTicketVersion()
  const duplicateActiveThreads = useDuplicateActiveThreads(sourceTicket?.id)
  const [isCreating, setIsCreating] = useState(false)
  const [versionJustification, setVersionReason] = useState('')
  const [copyThreads, setCopyThreads] = useState(false)
  const [messageApi, contextHolder] = message.useMessage()

  const handleConfirm = useCallback(async () => {
    if (!sourceTicket) {
      return
    }

    setIsCreating(true)
    try {
      const newTicketId = await createTicketVersion({
        sourceTicket,
        versionJustification: versionJustification.trim() || undefined,
      })

      if (copyThreads) {
        try {
          await duplicateActiveThreads(newTicketId, sourceTicket)
        } catch (error) {
          console.warn('Failed to duplicate threads to new version', error)
        }
      }

      messageApi.success(
        `Created version ${sourceTicket.version + 1} based on version ${sourceTicket.version}`
      )
      await onSuccess(newTicketId, sourceTicket)
      setVersionReason('')
      setCopyThreads(false)
      onClose()
    } catch (error) {
      console.error('Failed to create ticket version', error)
      messageApi.error('Failed to create a new ticket version')
    } finally {
      setIsCreating(false)
    }
  }, [
    copyThreads,
    createTicketVersion,
    duplicateActiveThreads,
    messageApi,
    onClose,
    onSuccess,
    sourceTicket,
    versionJustification,
  ])

  return (
    <>
      {contextHolder}
      <Modal
        title='Create New Ticket Version'
        open={isOpen}
        onOk={handleConfirm}
        onCancel={() => {
          setVersionReason('')
          onClose()
        }}
        okText='Create Version'
        cancelText='Cancel'
        confirmLoading={isCreating}>
        <ContentStack>
          <Alert
            type='info'
            showIcon
            message='The current version will remain unchanged and read-only.'
          />
          <Text type='secondary'>
            The new version is pre-filled with all data from the current
            version. You can review and edit any fields before completing it.
          </Text>
          <Checkbox
            checked={copyThreads}
            onChange={(e) => setCopyThreads(e.target.checked)}>
            Copy comment threads to new version
          </Checkbox>
          <div>
            <Text strong>Reason for new version (optional)</Text>
            <TextArea
              value={versionJustification}
              onChange={(e) => setVersionReason(e.target.value)}
              placeholder='e.g. Updated coverage assumptions, revised zone boundaries...'
              rows={3}
              maxLength={500}
              showCount
              style={{ marginTop: 4 }}
            />
          </div>
        </ContentStack>
      </Modal>
    </>
  )
}

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 8px;
`
